import {
  getStateHandle,
  getOktaUserId,
  getOktaWidgetModel,
  showFormErrors,
  startSpinnerAndHideForm,
  stopSpinner,
  stopSpinnerAndShowForm,
  getUsername
} from '../../utils/widget-facade';
import { getAnetUsername, isAnetUser, isProdUserInSupport } from '../../utils/user-utils';
import AmplitudeUtils from '../../utils/amplitude-utils';
import { ERROR_KEYS, ERROR_MESSAGES } from '../../constants';
import { setSecurityQuestionsPromptCookies } from '../../utils/storage-utils';
import PasswordResetBaseCustomizer from './base';
import HeaderWithEventHelper from '../../components/common/Header';
import SymantecCode from '../../components/password-reset/SymantecCode';
import BlockProdUserPasswordResetInSupportBanner from '../../components/password-reset/BlockPasswordResetForProdUserInSupport';
import { createWrapper } from '../../utils/react-utils';
import { passwordReset, shouldPromptForSymantecVIP } from '../../utils/anet-client';
import {
  getTransformedError
} from '../../utils/password-reset-api';
import athenaConfig from '../../config';

interface SymantecPromptPayload {
  username: string;
  stateHandle: string;
}

export default class PasswordResetAnetCustomizer extends PasswordResetBaseCustomizer {
  protected shouldPromptForSymantec: boolean = false;
  protected symantecFieldIndex = 0;

  constructor(args: any) {
    super(args);
    this.symantecFieldIndex = 0;
  }

  protected getSymantecSecurityCode() {
    if (this.shouldPromptForSymantec) {
      return (document.getElementsByName('credentials.securitycode')[0] as HTMLInputElement).value;
    }
  }

  protected isSecurityCodeValid() {
    const { shouldPromptForSymantec } = this;
    const emptyFieldErrorMessage = ERROR_MESSAGES['error.field.empty'];
    if (shouldPromptForSymantec && !this.getSymantecSecurityCode()) {
      //This is our custom field and thus, a special case, and needs raising the error separately
      this.setErrorForSymantecField({ errorMessage: emptyFieldErrorMessage });
      return false;
    }
    return true;
  }

  protected clearPasswordResetForm() {
    (document.getElementsByClassName('o-form o-form-edit-mode')[0] as HTMLFormElement).reset();
    this.clearSymantecCode();
  }

  protected isSymantecValidationError(errorMessage: string) {
    return errorMessage?.startsWith('symantec.');
  }

  protected clearSymantecCode() {
    this.eventEmitter.emitEventToComponent({
      componentName: SymantecCode.displayName as string,
      eventName: 'clear-value',
    });
  }

  private setErrorForSymantecField({ errorMessage }: { errorMessage: string }) {
    this.eventEmitter.emitEventToComponent({
      componentName: SymantecCode.displayName as string,
      eventName: 'update-error',
      eventPayload: { errorMessage },
    });
  }

  protected blockPasswordUpdateForProdUser() {
    const wrapper = createWrapper({ hidden: false });
    const formContainer: JQuery<HTMLElement> = jQueryCourage('[data-se="o-form-content"]');
    formContainer.append(wrapper);
    this.renderReactComponent({
      id: 'block-prod-user-in-support',
      container: wrapper,
      component: <BlockProdUserPasswordResetInSupportBanner />,
    });
    stopSpinner();
  }

  protected onPasswordReset() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const {
      oktaSignIn: { authClient },
    } = this;
    const originalProceed = authClient.idx.proceed.bind(authClient);

    // Widget calls this method after performing frontend validation with the expected payload
    // See https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/controllers/FormController.ts#L284
    authClient.idx.proceed = async (...args: any) => {
      const model = getOktaWidgetModel();
      //Handle errors by listing to error events once the button is clicked. This helps in receiving a more "normalized"
      //error payload - See https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/controllers/FormController.ts#L385
      model.once('error', (_: any, error: any) => {
        if (this.isSymantecValidationError(error.responseJSON?.message)) {
          this.clearPasswordResetForm();
        }
        else {
          this.clearSymantecCode();
        }
        const errorMessage =
          error?.responseJSON?.errorCauses?.[0]?.errorSummary ??
          error?.responseJSON?.xhr?.responseJSON?.errorSummary ??
          error?.errorSummary ??
          ERROR_MESSAGES[ERROR_KEYS.GENERIC_INTERNAL_ERROR];
        AmplitudeUtils.logPasswordResetEvent('PasswordChangeFailed', {
          errorMessage,
          shouldPromptForSymantec: self.shouldPromptForSymantec,
        });
      });

      AmplitudeUtils.logPasswordResetEvent('PasswordChangeInitiated', {
        shouldPromptForSymantec: self.shouldPromptForSymantec,
      });
      const { step } = args[0];
      if ([ 'reenroll-authenticator-warning', 'reenroll-authenticator' ].includes(step)) {
        const {
          credentials: { passcode },
          stateHandle,
        } = args[0];
        const symantecSecurityCode = this.getSymantecSecurityCode();
        const response = await self.triggerPasswordResetRequest({
          symantecSecurityCode,
          newPassword: passcode,
          stateHandle,
        });
        if (response.sqtoken) {
          setSecurityQuestionsPromptCookies({
            sqtoken: response.sqtoken,
            primaryAuthWithCustomTxn: true,
          });
        }

        const requiresMfa = response.remediation?.value?.some(
          (r: any) => r.name === 'select-authenticator-authenticate' || r.name === 'select-authenticator-enroll'
        );
        const requestSucceeded = athenaConfig.featureFlags.enableExpiredPasswordResetViaLambda ?
          !!response.success : !response.error;

        if (requestSucceeded || requiresMfa) {
          AmplitudeUtils.logPasswordResetEvent('PasswordChangeSuccess', {
            shouldPromptForSymantec: self.shouldPromptForSymantec,
          });
        }
        const passwordUpdateSucceeded = requestSucceeded || requiresMfa;

        const idxResponse = await authClient.idx.makeIdxResponse(response, {}, passwordUpdateSucceeded);
        await authClient.transactionManager.saveIdxResponse({
          rawIdxResponse: idxResponse,
          requestDidSucceed: passwordUpdateSucceeded,
          stateHandle,
        });
        return idxResponse;
      }
      return originalProceed.apply(authClient, args);
    };
  }

  /**
   * Override additionalValidation to add Symantec code validation.
   */
  protected additionalValidation(): Record<string, string> | undefined {
    const symantecCodeValid = this.isSecurityCodeValid();
    // return a hash when symantec token is not valid so widget set the top error banner
    return symantecCodeValid ? undefined : { 'symantec-token': 'error' };
  }

  protected customizePasswordResetButton() {
    // Use base class validation wrapper which includes password length check
    this.wrapValidationWithPasswordLengthCheck();
    this.onPasswordReset();
  }

  async shouldCustomize() {
    return (await super.shouldCustomize()) && isAnetUser();
  }

  async customize() {
    try {
      startSpinnerAndHideForm();
      const prodUserInSupport = isProdUserInSupport();
      if (prodUserInSupport && (this.context.formName === 'reenroll-authenticator-warning')) {
        this.skipPasswordResetWarningForProdUser();
        return;
      }
      await super.customize();
      if (prodUserInSupport) {
        this.blockPasswordUpdateForProdUser();
        return;
      }
      await this.handleSymantecPrompt();
      stopSpinnerAndShowForm();
    }
    catch (error: any) {
      await this.handleSymantecPromptError(error);
    }
  }

  async getSymantecPromptPayload(): Promise<SymantecPromptPayload> {
    return {
      username: athenaConfig.featureFlags.enableExpiredPasswordResetViaLambda ? getUsername() : getAnetUsername(),
      stateHandle: getStateHandle(),
    };
  }

  async handleSymantecPromptError(error: any): Promise<void> {
    const errorMessage = error.messages?.value?.[0]?.message ?? error.errorSummary ?? error.message;
    AmplitudeUtils.logPasswordResetEvent('SymantecVipPromptFailure', {
      errorMessage,
    });
    error.errorSummary = errorMessage === 'Failed to fetch' ? ERROR_MESSAGES[ERROR_KEYS.PASSWORD_RESET_LAMBDA_ERROR] : errorMessage;
    showFormErrors(error);
    stopSpinner();
  }

  async triggerPasswordResetRequest({ newPassword, symantecSecurityCode }: any): Promise<any> {
    try {
      const symantecPromptPayload = await this.getSymantecPromptPayload();
      return await passwordReset({
        newPassword,
        symantecSecurityCode,
        ...symantecPromptPayload,
        clientId: athenaConfig.featureFlags.enableExpiredPasswordResetViaLambda ? undefined : this.settings.clientId,
      });
    }
    catch (error: any) {
      // Throw error to show message on the error banner
      throw getTransformedError(error);
    }
  }

  protected async shouldPromptForSymantecVIP(payload: any): Promise<boolean> {
    return shouldPromptForSymantecVIP(payload);
  }

  protected async handleSymantecPrompt() {
    const checkPayload = await this.getSymantecPromptPayload();
    this.shouldPromptForSymantec = await this.shouldPromptForSymantecVIP(checkPayload);
    if (this.shouldPromptForSymantec) {
      this.customizeForEpcs();
      AmplitudeUtils.logPasswordResetEvent('SymantecVipPrompted', {
        userId: getOktaUserId(),
      });
    }
  }
  protected getSymantecCodeTarget(): JQuery {
    return jQueryCourage('#athena-credentials\\.passcode-input-field-container');
  }

  protected customizeForEpcs() {
    const headerDescription =
      'Two-factor authentication is required to change your password or manage your credentialed devices.';
    this.eventEmitter.emitEventToComponent({
      componentName: HeaderWithEventHelper.displayName as string,
      eventName: 'update-header-description',
      eventPayload: { headerDescription },
    });
    const container = createWrapper({ hidden: false });
    this.getSymantecCodeTarget().before(container);
    const component = <SymantecCode />;
    this.renderReactComponent({ id: 'symantec-token', component, container });
  }

  protected skipPasswordResetWarningForProdUser() {
    jQueryCourage('[data-se="skip"]').trigger('click');
    stopSpinner();
  }

}
